import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { maxBy, minBy, sorted } from "helpers";
import { EventType } from "src/store/api/generatedApi";

dayjs.extend(isSameOrAfter);

interface IdentifiedStart {
  id: string;
  start: Dayjs;
}

/**
 * The periods that contain a start an edit touched: an edited event's current or
 * original start, or the original start of an event that was deleted.
 */
export const periodsTouchedByEdits = <Period>({
  periods,
  periodStart,
  periodEnd,
  currentEvents,
  originalEvents,
  modifiedEventIds,
}: {
  periods: Period[];
  periodStart: (period: Period) => Dayjs;
  periodEnd: (period: Period) => Dayjs;
  currentEvents: IdentifiedStart[];
  originalEvents: IdentifiedStart[];
  modifiedEventIds: Set<string>;
}): Period[] => {
  const originalStartById = new Map(
    originalEvents.map((event) => [event.id, event.start])
  );
  const currentEventIds = new Set(currentEvents.map((event) => event.id));

  const editedStarts = [
    ...currentEvents
      .filter((event) => modifiedEventIds.has(event.id))
      .flatMap((event) => {
        const originalStart = originalStartById.get(event.id);
        return originalStart ? [event.start, originalStart] : [event.start];
      }),
    ...originalEvents
      .filter((event) => !currentEventIds.has(event.id))
      .map((event) => event.start),
  ];

  return periods.filter((period) => {
    const start = periodStart(period);
    const end = periodEnd(period);
    return editedStarts.some(
      (editedStart) =>
        editedStart.isSameOrAfter(start) && editedStart.isBefore(end)
    );
  });
};

export const eventStartsInGap = <Event extends { start: Dayjs }>({
  events,
  originalEvents,
  startIsInGap,
}: {
  events: Event[];
  originalEvents: Event[];
  startIsInGap: (start: Dayjs) => boolean;
}): boolean =>
  events.some((event) => startIsInGap(event.start)) ||
  originalEvents.some((event) => startIsInGap(event.start));

export const shouldRouteToSubmitEdits = ({
  unverifiedPeriodCount,
  verifyPeriodStartCount,
  hasEdits,
  hasGapEdits,
}: {
  unverifiedPeriodCount: number;
  verifyPeriodStartCount: number;
  hasEdits: boolean;
  hasGapEdits: boolean;
}): boolean =>
  unverifiedPeriodCount === 0 &&
  verifyPeriodStartCount === 0 &&
  hasEdits &&
  !hasGapEdits;

export const verifyWindowSeedPeriods = <Period>({
  periods,
  isVerified,
  periodStart,
  periodEnd,
  currentEvents,
  originalEvents,
  modifiedEventIds,
}: {
  periods: Period[];
  isVerified: (period: Period) => boolean;
  periodStart: (period: Period) => Dayjs;
  periodEnd: (period: Period) => Dayjs;
  currentEvents: IdentifiedStart[];
  originalEvents: IdentifiedStart[];
  modifiedEventIds: Set<string>;
}): Period[] => {
  const touched = new Set(
    periodsTouchedByEdits({
      periods,
      periodStart,
      periodEnd,
      currentEvents,
      originalEvents,
      modifiedEventIds,
    })
  );
  return periods.filter((period) =>
    broughtInPeriodWillVerify({
      isVerified: isVerified(period),
      edited: touched.has(period),
    })
  );
};

/**
 * Whether an event's start falls in `[periodStart, periodEnd)`. The end is exclusive,
 * matching the backend rule that assigns an event starting exactly on a boundary to the
 * later period.
 */
export const eventStartsInPeriod = ({
  start,
  periodStart,
  periodEnd,
}: {
  start: Dayjs;
  periodStart: Dayjs;
  periodEnd: Dayjs;
}): boolean => !start.isBefore(periodStart) && start.isBefore(periodEnd);

export const clampToRange = ({
  time,
  lowerBound,
  upperBound,
}: {
  time: Dayjs;
  lowerBound: Dayjs | undefined;
  upperBound: Dayjs | undefined;
}): Dayjs => {
  if (lowerBound && time.isBefore(lowerBound)) return lowerBound;
  if (upperBound && time.isAfter(upperBound)) return upperBound;
  return time;
};

export const clampStartToRange = ({
  time,
  lowerBound,
  upperBound,
}: {
  time: Dayjs;
  lowerBound: Dayjs | undefined;
  upperBound: Dayjs | undefined;
}): Dayjs => {
  const clamped = clampToRange({ time, lowerBound, upperBound });
  if (upperBound && !clamped.isBefore(upperBound)) {
    return upperBound.subtract(1, "second");
  }
  return clamped;
};

/** Keeps the last occurrence of each `id`, so a later copy (e.g. a brought-in event) wins
 * over an earlier one and no client id is serialised twice. */
export const dedupeById = <T extends { id: string }>(events: T[]): T[] => [
  ...events
    .reduce((byId, event) => byId.set(event.id, event), new Map<string, T>())
    .values(),
];

/**
 * The events a verify/submit save should send for a set of periods. An event is in scope
 * when its start falls in one of the periods or in an editable gap within the window.
 * In-window events (and their pre-edit originals) are drawn from the page context; each
 * brought-in off-window period supplies its own complete set, which wins over any
 * in-window copy of the same event so a boundary-straddling event is not sent twice. A
 * brought-in period's own deletion is honoured too: an id its original names but its
 * current set no longer carries is dropped from the page's contribution as well.
 */
export const buildVerifyScope = <
  Event extends { id: string; start: Dayjs },
  Period,
>({
  periods,
  currentEvents,
  originalEvents,
  broughtInEvents,
  broughtInOriginalEvents,
  periodStart,
  periodEnd,
  startIsInGap,
}: {
  periods: Period[];
  currentEvents: Event[];
  originalEvents: Event[];
  broughtInEvents: Event[];
  broughtInOriginalEvents: Event[];
  periodStart: (period: Period) => Dayjs;
  periodEnd: (period: Period) => Dayjs;
  startIsInGap: (start: Dayjs) => boolean;
}): { eventsInScope: Event[]; originalEventsInScope: Event[] } => {
  const startIsInScope = (start: Dayjs): boolean =>
    periods.some((period) =>
      eventStartsInPeriod({
        start,
        periodStart: periodStart(period),
        periodEnd: periodEnd(period),
      })
    ) || startIsInGap(start);

  const idsInScope = new Set<string>([
    ...currentEvents
      .filter((event) => startIsInScope(event.start))
      .map((event) => event.id),
    ...originalEvents
      .filter((event) => startIsInScope(event.start))
      .map((event) => event.id),
  ]);
  const broughtInIds = new Set(broughtInEvents.map((event) => event.id));
  const broughtInOriginalIds = new Set(
    broughtInOriginalEvents.map((event) => event.id)
  );
  const broughtInDeletedIds = new Set(
    broughtInOriginalEvents
      .filter((event) => !broughtInIds.has(event.id))
      .map((event) => event.id)
  );

  return {
    eventsInScope: [
      ...currentEvents.filter(
        (event) =>
          idsInScope.has(event.id) &&
          !broughtInIds.has(event.id) &&
          !broughtInDeletedIds.has(event.id)
      ),
      ...broughtInEvents,
    ],
    originalEventsInScope: [
      ...originalEvents.filter(
        (event) =>
          idsInScope.has(event.id) && !broughtInOriginalIds.has(event.id)
      ),
      ...broughtInOriginalEvents,
    ],
  };
};

export type VerificationChange = "newlyVerified" | "reVerified" | "unchanged";

/**
 * For each period in a window, what a save does to its verification: `reVerified`
 * (already verified and edited this save), `newlyVerified` (unverified but a corrected
 * event lands in it), or `unchanged`.
 */
export const classifyWindowVerification = <Period>({
  windowPeriods,
  periodStart,
  periodEnd,
  isVerified,
  isReVerified,
  correctedEventStarts,
}: {
  windowPeriods: Period[];
  periodStart: (period: Period) => Dayjs;
  periodEnd: (period: Period) => Dayjs;
  isVerified: (period: Period) => boolean;
  isReVerified: (period: Period) => boolean;
  correctedEventStarts: Dayjs[];
}): { period: Period; change: VerificationChange }[] =>
  windowPeriods.map((period) => {
    if (isReVerified(period)) {
      return { period, change: "reVerified" };
    }
    const start = periodStart(period);
    const end = periodEnd(period);
    const gainsAnEvent = correctedEventStarts.some(
      (eventStart) =>
        eventStart.isSameOrAfter(start) && eventStart.isBefore(end)
    );
    if (!isVerified(period) && gainsAnEvent) {
      return { period, change: "newlyVerified" };
    }
    return { period, change: "unchanged" };
  });

type FindNextLinked<Period> = (scope: {
  scopePeriodIds: ReadonlySet<number>;
  resolvedPeriodIds: ReadonlySet<number>;
}) => Period | undefined;

export type BringInDecision = "in" | "out" | "back" | "cancel";

// A hop's resolution: the modal's decision, or `inWithoutPage` — an acceptance made
// without showing a page, for a period already shown in full on the previous page.
// A step back skips page-less hops, and a forward replay may re-affirm them silently.
export type BringInHopDecision = BringInDecision | "inWithoutPage";

export interface BringInProgress {
  index: number;
  canGoBack: boolean;
  // Whether this hop may be accepted without showing a page: a fresh forward hop, or a
  // replay of a hop accepted that way before. False when a step back (or the forward
  // replay after one) returns to a hop that was shown as a page, which must reopen as one.
  canBypassPage: boolean;
}

// A defensive backstop on `resolvePairChain`'s walk (`maxHops` below) — a generous
// ceiling, not tuned to normal use.
export const MAX_CHAIN_HOPS = 20;

/** `resolvePairChain`'s outcome — see its docstring for when each variant applies. */
export type PairChainResult =
  { outcome: "resolved"; scope: Set<number> } | { outcome: "capped" };

/**
 * Grows a seed set of periods one linked period at a time. `findNextLinked` reports
 * the next review period a lift/drop pair reaches into from the current scope (whether
 * loaded or across the window edge); `bringInPeriod` decides whether to pull it in
 * (`in`), leave it out (`out`), or step back to the previous hop (`back`). An accepted
 * period joins the scope and may reveal a further link; a declined one is left out.
 * `cancel` abandons the remaining chain, keeping the periods already accepted — a
 * distinct, deliberate partial-save, unlike `capped` below. `findNextLinked` is
 * consulted on each new hop, and again from a hop whose acceptance changed (later hops
 * are recomputed), so an edit `bringInPeriod` makes to the pairing changes which link
 * comes next. A step back lands on the nearest earlier hop that was shown as a page,
 * skipping hops accepted without one; the forward replay that follows re-shows pages
 * and may re-affirm page-less acceptances silently. `bringInPeriod` receives the hop's
 * position for a progress indicator, and the scope accepted before it. `maxHops` caps
 * how many *new* hops the walk discovers (replaying hops already found is unaffected)
 * — a backstop against a runaway chain, not expected to trigger normally; hitting it
 * reports `{ outcome: "capped" }`, since a chain cut short there is not a result any
 * caller should treat as complete.
 */
export const resolvePairChain = async <Period>({
  seedPeriodIds,
  findNextLinked,
  periodId,
  bringInPeriod,
  maxHops,
}: {
  seedPeriodIds: Iterable<number>;
  findNextLinked: FindNextLinked<Period>;
  periodId: (period: Period) => number;
  bringInPeriod: (
    period: Period,
    progress: BringInProgress,
    // The periods currently accepted into the chain — at any hop, not only those before
    // this one, so a page re-shown by a step back still seeds and marks the accepted
    // hops after it. Declined periods and hops dropped by a decision change are excluded.
    acceptedPeriodIds: ReadonlySet<number>
  ) => Promise<BringInHopDecision>;
  maxHops: number;
}): Promise<PairChainResult> => {
  const seed = new Set<number>(seedPeriodIds);
  const hops: Period[] = [];
  const decisions: ("in" | "out" | "inWithoutPage")[] = [];
  const isAccepted = (
    decision: "in" | "out" | "inWithoutPage" | undefined
  ): boolean => decision === "in" || decision === "inWithoutPage";

  const scopeAt = (count: number): Set<number> =>
    hops
      .slice(0, count)
      .reduce(
        (scope, period, index) =>
          isAccepted(decisions[index]) ? scope.add(periodId(period)) : scope,
        new Set<number>(seed)
      );
  const resolvedAt = (count: number): Set<number> =>
    hops
      .slice(0, count)
      .reduce(
        (resolved, period, index) =>
          decisions[index] === "out"
            ? resolved.add(periodId(period))
            : resolved,
        scopeAt(count)
      );

  let cursor = 0;
  for (;;) {
    const isNewHop = cursor === hops.length;
    if (isNewHop) {
      if (hops.length >= maxHops) return { outcome: "capped" };
      const next = findNextLinked({
        scopePeriodIds: scopeAt(cursor),
        resolvedPeriodIds: resolvedAt(cursor),
      });
      if (!next) break;
      hops.push(next);
    }
    const period = hops[cursor];
    if (period === undefined) break;
    const previousDecision = decisions[cursor];
    const decision = await bringInPeriod(
      period,
      {
        index: cursor + 1,
        canGoBack: cursor > 0,
        canBypassPage: isNewHop || previousDecision === "inWithoutPage",
      },
      scopeAt(hops.length)
    );
    if (decision === "cancel") {
      return { outcome: "resolved", scope: scopeAt(cursor) };
    }
    if (decision === "back") {
      cursor = Math.max(0, cursor - 1);
      // Page-less hops have nothing to reopen; land on the previous shown page.
      while (cursor > 0 && decisions[cursor] === "inWithoutPage") cursor -= 1;
      continue;
    }
    decisions[cursor] = decision;
    // A changed acceptance invalidates the hops derived after it, so recompute forward;
    // an acceptance merely switching between paged and page-less leaves the scope — and
    // so the links derived from it — unchanged.
    if (isAccepted(previousDecision) !== isAccepted(decision)) {
      hops.length = cursor + 1;
      decisions.length = cursor + 1;
    }
    cursor += 1;
  }

  return { outcome: "resolved", scope: scopeAt(hops.length) };
};

/**
 * Whether a set of events was edited relative to its seed: an event differing from its
 * seed copy (or added, so it has none), or a seed event no longer present (deleted).
 */
export const eventsEditedAgainstSeed = <Event extends { id: string }>({
  events,
  seedEvents,
  seedById,
  eventDiffersFromSeed,
}: {
  events: Event[];
  seedEvents: Event[];
  seedById: Map<string, Event>;
  eventDiffersFromSeed: (event: Event, seed: Event | undefined) => boolean;
}): boolean => {
  const currentIds = new Set(events.map((event) => event.id));
  return (
    events.some((event) =>
      eventDiffersFromSeed(event, seedById.get(event.id))
    ) || seedEvents.some((seed) => !currentIds.has(seed.id))
  );
};

/**
 * The ids an edit touched, relative to the stored events: an event that differs from its
 * original copy, an added event (no original), or an original no longer present (deleted).
 */
export const collectEditedEventIds = <Event extends { id: string }>({
  currentEvents,
  originalEvents,
  eventDiffersFromOriginal,
}: {
  currentEvents: Event[];
  originalEvents: Event[];
  eventDiffersFromOriginal: (
    event: Event,
    original: Event | undefined
  ) => boolean;
}): Set<string> => {
  const originalById = new Map(
    originalEvents.map((event) => [event.id, event])
  );
  const currentIds = new Set(currentEvents.map((event) => event.id));
  return new Set([
    ...currentEvents
      .filter((event) =>
        eventDiffersFromOriginal(event, originalById.get(event.id))
      )
      .map((event) => event.id),
    ...originalEvents
      .filter((event) => !currentIds.has(event.id))
      .map((event) => event.id),
  ]);
};

/**
 * Whether a save writes a brought-in period's verification: an unverified period always
 * verifies, an already-verified one only when it was edited, keeping an untouched
 * neighbour in its existing generation.
 */
export const broughtInPeriodWillVerify = ({
  isVerified,
  edited,
}: {
  isVerified: boolean;
  edited: boolean;
}): boolean => !isVerified || edited;

/**
 * Of a resolved bring-in scope, the periods whose verification the save should write.
 * A seed (in-window) period always counts; a brought-in period counts per
 * `broughtInPeriodWillVerify`. `broughtInEdited` maps a brought-in period's id to
 * whether it was edited; a period absent from it is a seed period.
 */
export const periodsToPersist = <Period>({
  periods,
  isVerified,
  periodId,
  broughtInEdited,
}: {
  periods: Period[];
  isVerified: (period: Period) => boolean;
  periodId: (period: Period) => number;
  broughtInEdited: ReadonlyMap<number, boolean>;
}): Period[] =>
  periods.filter((period) => {
    const edited = broughtInEdited.get(periodId(period));
    if (edited === undefined) return true;
    return broughtInPeriodWillVerify({
      isVerified: isVerified(period),
      edited,
    });
  });

// Headroom above one review period's width, so a charge idling close to a full
// period doesn't trip the bound purely because of where the period grid falls.
export const SEQUENCE_GAP_HEADROOM_FACTOR = 2;

/**
 * The longest idle time between two consecutive lift/drop movements that still
 * counts as one charge sequence (see `groupLiftDropSequences`), given a tenant's
 * review period width.
 */
export const maxSequenceGapSecondsFromChunkSeconds = (
  chunkSeconds: number
): number => chunkSeconds * SEQUENCE_GAP_HEADROOM_FACTOR;

/**
 * Groups lift/drop events into time-ordered charge sequences: a new sequence starts
 * whenever a lift immediately follows a drop, or whenever the idle time since the
 * previous movement ended reaches `maxSequenceGapSeconds`, regardless of direction —
 * so a lift followed by more than one drop, or several lifts bunched ahead of their
 * drops, stays one sequence, but a run of same-direction events spread far enough
 * apart in time splits into separate ones. Two movements sharing an identical start
 * sort by direction (lift before drop), not by their order in `events`, so equal
 * timestamps group the same way regardless of input order. `flag` events play no
 * part in this and are dropped entirely, never carried into whichever sequence
 * happens to be open.
 *
 * Deliberately independent of a `ScrapEyesEvent`'s own `pairId` (an index-adjacency
 * identity mass-window matching and the timeline's pairing connector still key off) —
 * the two can disagree, since a sequence of more than two events has no single
 * `pairId` to carry.
 */
export const groupLiftDropSequences = <
  T extends { eventType: EventType; start: Dayjs; end: Dayjs },
>({
  events,
  maxSequenceGapSeconds,
}: {
  events: T[];
  maxSequenceGapSeconds: number;
}): T[][] => {
  const movements = sorted(
    events.filter(
      (event) => event.eventType === "lift" || event.eventType === "drop"
    ),
    (event) => [event.start.valueOf(), event.eventType === "drop" ? 1 : 0]
  );
  return movements.reduce<T[][]>((groups, event) => {
    const previousGroup = groups.at(-1);
    const previousEvent = previousGroup?.at(-1);
    const startsNewSequence =
      (event.eventType === "lift" && previousEvent?.eventType === "drop") ||
      (previousEvent !== undefined &&
        event.start.diff(previousEvent.end, "second") >= maxSequenceGapSeconds);
    if (previousGroup === undefined || startsNewSequence) {
      return [...groups, [event]];
    }
    return [...groups.slice(0, -1), [...previousGroup, event]];
  }, []);
};

/**
 * Splits lift/drop events into those whose partner falls beyond the loaded events: a
 * trailing run with no drop at all is left open forward, its drop beyond the loaded
 * end; a leading run with no lift at all is left open backward, its lift before the
 * loaded start. Every event of such a run is returned, not just its first or last.
 * Only the very last group counts towards `danglingLifts`, and only the very first
 * towards `danglingDrops` — an all-lift or all-drop group elsewhere in the pool is a
 * genuine anomaly in the data, not a window-edge effect, and is left unflagged here.
 */
export const danglingWindowEdgeEvents = <
  T extends { eventType: EventType; start: Dayjs; end: Dayjs },
>({
  events,
  maxSequenceGapSeconds,
}: {
  events: T[];
  maxSequenceGapSeconds: number;
}): { danglingLifts: T[]; danglingDrops: T[] } => {
  const groups = groupLiftDropSequences({ events, maxSequenceGapSeconds });
  const firstGroup = groups[0];
  const lastGroup = groups.at(-1);
  return {
    danglingLifts: lastGroup?.every((event) => event.eventType === "lift")
      ? lastGroup
      : [],
    danglingDrops: firstGroup?.every((event) => event.eventType === "drop")
      ? firstGroup
      : [],
  };
};

export const earliestPeriodStartingAtOrAfter = <Period>({
  dayPeriods,
  boundary,
  periodStart,
}: {
  dayPeriods: Period[];
  boundary: Dayjs;
  periodStart: (period: Period) => Dayjs;
}): Period | undefined =>
  minBy(
    dayPeriods.filter((period) => !periodStart(period).isBefore(boundary)),
    (period) => periodStart(period).valueOf()
  );

export const latestPeriodEndingAtOrBefore = <Period>({
  dayPeriods,
  boundary,
  periodEnd,
}: {
  dayPeriods: Period[];
  boundary: Dayjs;
  periodEnd: (period: Period) => Dayjs;
}): Period | undefined =>
  maxBy(
    dayPeriods.filter((period) => !periodEnd(period).isAfter(boundary)),
    (period) => periodEnd(period).valueOf()
  );

interface CrossingEvent {
  id: string;
  start: Dayjs;
  end: Dayjs;
  eventType: EventType;
}

/**
 * The lift/drop sequences (see `groupLiftDropSequences`) an edit affects, from both the
 * current and the original events. A sequence is affected when it holds an edited event
 * (see `collectEditedEventIds`), or when the other version has no sequence of exactly the
 * same events: an edit that deletes or moves the event separating two sequences merges
 * them, and one that adds an event can split them. An unaffected sequence is unchanged
 * in both versions.
 */
export const affectedLiftDropSequences = <Event extends CrossingEvent>({
  currentEvents,
  originalEvents,
  eventDiffersFromOriginal,
  maxSequenceGapSeconds,
}: {
  currentEvents: Event[];
  originalEvents: Event[];
  eventDiffersFromOriginal: (
    event: Event,
    original: Event | undefined
  ) => boolean;
  maxSequenceGapSeconds: number;
}): Event[][] => {
  const editedIds = collectEditedEventIds({
    currentEvents,
    originalEvents,
    eventDiffersFromOriginal,
  });
  const membershipKey = (sequence: Event[]): string =>
    JSON.stringify(
      sorted(
        sequence.map((event) => event.id),
        (id) => id
      )
    );
  const currentSequences = groupLiftDropSequences({
    events: currentEvents,
    maxSequenceGapSeconds,
  });
  const originalSequences = groupLiftDropSequences({
    events: originalEvents,
    maxSequenceGapSeconds,
  });
  const affectedAgainst = (
    sequences: Event[][],
    otherSequences: Event[][]
  ): Event[][] => {
    const otherKeys = new Set(otherSequences.map(membershipKey));
    return sequences.filter(
      (sequence) =>
        sequence.some((event) => editedIds.has(event.id)) ||
        !otherKeys.has(membershipKey(sequence))
    );
  };
  return [
    ...affectedAgainst(currentSequences, originalSequences),
    ...affectedAgainst(originalSequences, currentSequences),
  ];
};

/**
 * The adjacent day period an affected lift/drop charge sequence (see
 * `affectedLiftDropSequences`) reaches into from `originPeriodId`, given the current and
 * original events loaded in a focus window. An unaffected sequence agrees with its
 * neighbour already, so it links nothing; an affected original sequence still links the
 * period it reached before a deletion or move. A sequence touches the origin if one of
 * its own events starts inside it, or if it spans cleanly over the origin (an event
 * before its start, another at/after its end). A touching sequence crosses into every
 * day period that holds one of its loaded events past that edge, and the nearest such
 * period not in view, in scope, or resolved is offered. Only when no touching sequence
 * has a loaded event past the edge does one left genuinely open there fall back to the
 * nearest day period beyond the viewing window `[windowStart, windowEnd)`. The left edge
 * is symmetric. When both edges cross at once, the period after wins arbitrarily (the
 * period before surfaces on a later call once resolved). `undefined` when nothing
 * crosses out.
 */
export const linkedPeriodFromCrossingPair = <
  Event extends CrossingEvent,
  Period,
>({
  focusEvents,
  focusOriginalEvents,
  eventDiffersFromOriginal,
  maxSequenceGapSeconds,
  dayPeriods,
  windowStart,
  windowEnd,
  periodStart,
  periodEnd,
  periodId,
  originPeriodId,
  scopePeriodIds,
  resolvedPeriodIds,
}: {
  focusEvents: Event[];
  focusOriginalEvents: Event[];
  eventDiffersFromOriginal: (
    event: Event,
    original: Event | undefined
  ) => boolean;
  maxSequenceGapSeconds: number;
  dayPeriods: Period[];
  windowStart: Dayjs;
  windowEnd: Dayjs;
  periodStart: (period: Period) => Dayjs;
  periodEnd: (period: Period) => Dayjs;
  periodId: (period: Period) => number;
  originPeriodId: number;
  scopePeriodIds: ReadonlySet<number>;
  resolvedPeriodIds: ReadonlySet<number>;
}): Period | undefined => {
  const originPeriod = dayPeriods.find(
    (period) => periodId(period) === originPeriodId
  );
  if (originPeriod === undefined) return undefined;
  const originStart = periodStart(originPeriod);
  const originEnd = periodEnd(originPeriod);
  const startsInOrigin = (start: Dayjs): boolean =>
    eventStartsInPeriod({
      start,
      periodStart: originStart,
      periodEnd: originEnd,
    });

  const touchesOrigin = (
    sequence: { start: Dayjs; eventType: EventType }[]
  ): boolean =>
    sequence.some((event) => startsInOrigin(event.start)) ||
    (sequence.some((event) => event.start.isBefore(originStart)) &&
      sequence.some((event) => !event.start.isBefore(originEnd)));
  const touchingSequences = affectedLiftDropSequences({
    currentEvents: focusEvents,
    originalEvents: focusOriginalEvents,
    eventDiffersFromOriginal,
    maxSequenceGapSeconds,
  }).filter(touchesOrigin);

  const periodContaining = (start: Dayjs): Period | undefined =>
    dayPeriods.find((period) =>
      eventStartsInPeriod({
        start,
        periodStart: periodStart(period),
        periodEnd: periodEnd(period),
      })
    );
  const overlapsWindow = (period: Period): boolean =>
    periodStart(period).isBefore(windowEnd) &&
    periodEnd(period).isAfter(windowStart);

  const isOfferable = (period: Period | undefined): period is Period =>
    period !== undefined &&
    !overlapsWindow(period) &&
    !scopePeriodIds.has(periodId(period)) &&
    !resolvedPeriodIds.has(periodId(period));
  const offerablePeriodsHolding = (events: Event[]): Period[] =>
    events.map((event) => periodContaining(event.start)).filter(isOfferable);

  const rightCrossingEvents = touchingSequences.flatMap((sequence) =>
    sequence.filter((event) => !event.start.isBefore(originEnd))
  );
  const hasOpenForward = touchingSequences.some((sequence) => {
    const last = sequence.at(-1);
    return last?.eventType === "lift" && startsInOrigin(last.start);
  });
  const rightFallback =
    rightCrossingEvents.length === 0 && hasOpenForward
      ? earliestPeriodStartingAtOrAfter({
          dayPeriods: dayPeriods.filter((period) => !overlapsWindow(period)),
          boundary: originEnd,
          periodStart,
        })
      : undefined;
  const rightTarget = minBy(
    [
      ...offerablePeriodsHolding(rightCrossingEvents),
      ...[rightFallback].filter(isOfferable),
    ],
    (period) => periodStart(period).valueOf()
  );

  const leftCrossingEvents = touchingSequences.flatMap((sequence) =>
    sequence.filter((event) => event.start.isBefore(originStart))
  );
  const hasOpenBackward = touchingSequences.some((sequence) => {
    const first = sequence[0];
    return first?.eventType === "drop" && startsInOrigin(first.start);
  });
  const leftFallback =
    leftCrossingEvents.length === 0 && hasOpenBackward
      ? latestPeriodEndingAtOrBefore({
          dayPeriods: dayPeriods.filter((period) => !overlapsWindow(period)),
          boundary: originStart,
          periodEnd,
        })
      : undefined;
  const leftTarget = maxBy(
    [
      ...offerablePeriodsHolding(leftCrossingEvents),
      ...[leftFallback].filter(isOfferable),
    ],
    (period) => periodStart(period).valueOf()
  );

  return rightTarget ?? leftTarget;
};

/**
 * The soonest-starting neighbour any of `originPeriodIds` reaches into (see
 * `linkedPeriodFromCrossingPair`), checked independently per origin so a caller with
 * several seed periods at once still finds whichever crosses out first. `undefined`
 * when none of them cross out.
 */
export const firstCrossingNeighbour = <Event extends CrossingEvent, Period>({
  focusEvents,
  focusOriginalEvents,
  eventDiffersFromOriginal,
  maxSequenceGapSeconds,
  dayPeriods,
  windowStart,
  windowEnd,
  periodStart,
  periodEnd,
  periodId,
  originPeriodIds,
  scopePeriodIds,
  resolvedPeriodIds,
}: {
  focusEvents: Event[];
  focusOriginalEvents: Event[];
  eventDiffersFromOriginal: (
    event: Event,
    original: Event | undefined
  ) => boolean;
  maxSequenceGapSeconds: number;
  dayPeriods: Period[];
  windowStart: Dayjs;
  windowEnd: Dayjs;
  periodStart: (period: Period) => Dayjs;
  periodEnd: (period: Period) => Dayjs;
  periodId: (period: Period) => number;
  originPeriodIds: ReadonlySet<number>;
  scopePeriodIds: ReadonlySet<number>;
  resolvedPeriodIds: ReadonlySet<number>;
}): Period | undefined =>
  minBy(
    [...originPeriodIds]
      .map((originPeriodId) =>
        linkedPeriodFromCrossingPair({
          focusEvents,
          focusOriginalEvents,
          eventDiffersFromOriginal,
          maxSequenceGapSeconds,
          dayPeriods,
          windowStart,
          windowEnd,
          periodStart,
          periodEnd,
          periodId,
          originPeriodId,
          scopePeriodIds,
          resolvedPeriodIds,
        })
      )
      .filter((period): period is Period => period !== undefined),
    (period) => periodStart(period).valueOf()
  );

/**
 * Extends a bring-in page's will-verify set with the unverified periods the chain is
 * certain to reach from it: each affected crossing sequence (see
 * `linkedPeriodFromCrossingPair`) from a will-verify period into an unverified one marks
 * that period too, and the walk repeats from it. A verified linked period is left
 * unmarked — the save only writes it if edited — and the walk does not continue through
 * it, though it never masks a crossing on the origin's other side.
 */
export const chainedWillVerifyPeriodIds = <
  Event extends CrossingEvent,
  Period,
>({
  focusEvents,
  focusOriginalEvents,
  eventDiffersFromOriginal,
  maxSequenceGapSeconds,
  dayPeriods,
  windowStart,
  windowEnd,
  periodStart,
  periodEnd,
  periodId,
  isVerified,
  willVerifyPeriodIds,
}: {
  focusEvents: Event[];
  focusOriginalEvents: Event[];
  eventDiffersFromOriginal: (
    event: Event,
    original: Event | undefined
  ) => boolean;
  maxSequenceGapSeconds: number;
  dayPeriods: Period[];
  windowStart: Dayjs;
  windowEnd: Dayjs;
  periodStart: (period: Period) => Dayjs;
  periodEnd: (period: Period) => Dayjs;
  periodId: (period: Period) => number;
  isVerified: (period: Period) => boolean;
  willVerifyPeriodIds: readonly number[];
}): Set<number> => {
  const grow = (
    marked: ReadonlySet<number>,
    dismissed: ReadonlySet<number>
  ): Set<number> => {
    const reached = [...marked].flatMap((originPeriodId) => {
      const linked = linkedPeriodFromCrossingPair({
        focusEvents,
        focusOriginalEvents,
        eventDiffersFromOriginal,
        maxSequenceGapSeconds,
        dayPeriods,
        windowStart,
        windowEnd,
        periodStart,
        periodEnd,
        periodId,
        originPeriodId,
        scopePeriodIds: marked,
        resolvedPeriodIds: new Set([...marked, ...dismissed]),
      });
      return linked === undefined ? [] : [linked];
    });
    if (reached.length === 0) return new Set(marked);
    return grow(
      new Set([
        ...marked,
        ...reached
          .filter((period) => !isVerified(period))
          .map((period) => periodId(period)),
      ]),
      new Set([
        ...dismissed,
        ...reached
          .filter((period) => isVerified(period))
          .map((period) => periodId(period)),
      ])
    );
  };
  return grow(new Set(willVerifyPeriodIds), new Set<number>());
};

/**
 * Of the day periods outside the viewing window and not yet part of the chain, those
 * whose events a bring-in page changed relative to its seed: an event that differs from
 * its seed copy, one added on the page, or a seed event the page deleted. A reviewer's
 * edit to a visible neighbour marks it for verification even when no lift/drop pair
 * crosses into it, so such a period joins the chain like a crossing-linked one.
 */
export const offWindowPeriodsEditedInFocus = <
  Event extends { id: string; start: Dayjs },
  Period,
>({
  focusEvents,
  focusSeedEvents,
  eventDiffersFromSeed,
  dayPeriods,
  windowStart,
  windowEnd,
  periodStart,
  periodEnd,
  periodId,
  scopePeriodIds,
  resolvedPeriodIds,
}: {
  focusEvents: Event[];
  focusSeedEvents: Event[];
  eventDiffersFromSeed: (event: Event, seed: Event | undefined) => boolean;
  dayPeriods: Period[];
  windowStart: Dayjs;
  windowEnd: Dayjs;
  periodStart: (period: Period) => Dayjs;
  periodEnd: (period: Period) => Dayjs;
  periodId: (period: Period) => number;
  scopePeriodIds: ReadonlySet<number>;
  resolvedPeriodIds: ReadonlySet<number>;
}): Period[] => {
  const eligiblePeriods = dayPeriods.filter(
    (period) =>
      !(
        periodStart(period).isBefore(windowEnd) &&
        periodEnd(period).isAfter(windowStart)
      ) &&
      !scopePeriodIds.has(periodId(period)) &&
      !resolvedPeriodIds.has(periodId(period))
  );
  const seedById = new Map(focusSeedEvents.map((event) => [event.id, event]));
  const modifiedEventIds = new Set(
    focusEvents
      .filter((event) => eventDiffersFromSeed(event, seedById.get(event.id)))
      .map((event) => event.id)
  );
  return periodsTouchedByEdits({
    periods: eligiblePeriods,
    periodStart,
    periodEnd,
    currentEvents: focusEvents,
    originalEvents: focusSeedEvents,
    modifiedEventIds,
  });
};

export type BringInPeriodDisplayState = "reVerify" | "verify" | "context";

/**
 * How a period shown in the bring-in modal should read, from what confirming will do to it:
 * `reVerify` (a verified period this save writes again), `verify` (an unverified period this
 * save verifies), or `context` (shown around the target but left untouched — an unrelated
 * visible neighbour, or a verified period no edit reaches). `willVerify` is whether the save
 * already counts the period (a seed, or a brought-in period the persistence rule keeps);
 * `isEdited` catches a period the reviewer changes in this modal that the save has not
 * counted yet. So the in-view seeds a bring-in modal shows as context read as `verify`,
 * not faded, and a verified period the reviewer edits turns amber.
 */
export const bringInPeriodDisplayState = ({
  isVerified,
  isEdited,
  willVerify,
}: {
  isVerified: boolean;
  isEdited: boolean;
  willVerify: boolean;
}): BringInPeriodDisplayState => {
  if (!willVerify && !isEdited) return "context";
  return isVerified ? "reVerify" : "verify";
};

/**
 * The seed adjustments a bring-in page applies over its fetched events. `seedOverrides` are
 * the edits the page and the chain hold over the focus window, replacing the fetched copy of
 * the same id — a carried (chain) copy wins over the page copy. Only events an ancestor has
 * actually changed are carried: an untouched one is identical to what the fetch returns, so
 * overriding it would say the surface holds an edit when it holds none.
 * `omittedOriginalEventIds` are originals overlapping the focus whose event no longer exists
 * in either source, so a deletion holds on the new page. Each is `undefined` when empty.
 */
export const focusSeedAdjustments = <
  Event extends { id: string; start: Dayjs; end: Dayjs },
>({
  pageEvents,
  carriedEvents,
  pageOriginalEvents,
  carriedOriginalEvents,
  editedEventIds,
  focusStart,
  focusEnd,
}: {
  pageEvents: Event[];
  carriedEvents: Event[];
  pageOriginalEvents: Event[];
  carriedOriginalEvents: Event[];
  // The ids an ancestor surface has changed, added, or otherwise differs from its own fetch
  // on. Events outside it are left to the focus window's own fetch.
  editedEventIds: ReadonlySet<string>;
  focusStart: Dayjs;
  focusEnd: Dayjs;
}): {
  seedOverrides: Event[] | undefined;
  omittedOriginalEventIds: Set<string> | undefined;
} => {
  const overlapsFocus = (event: Event): boolean =>
    event.start.isBefore(focusEnd) && event.end.isAfter(focusStart);
  const isEdited = (event: Event): boolean => editedEventIds.has(event.id);
  // Deduped because an override whose id the focus fetch does not return — an added event
  // always, since its id is a client one — is appended rather than replaced, so a second
  // copy would render as a second event at the same bounds.
  const overlapping = dedupeById([
    ...pageEvents.filter((event) => overlapsFocus(event) && isEdited(event)),
    ...carriedEvents.filter((event) => overlapsFocus(event) && isEdited(event)),
  ]);
  const currentIds = new Set(
    [...pageEvents, ...carriedEvents].map((event) => event.id)
  );
  const omitted = new Set(
    [...pageOriginalEvents, ...carriedOriginalEvents]
      .filter(
        (original) => !currentIds.has(original.id) && overlapsFocus(original)
      )
      .map((original) => original.id)
  );
  return {
    seedOverrides: overlapping.length > 0 ? overlapping : undefined,
    omittedOriginalEventIds: omitted.size > 0 ? omitted : undefined,
  };
};

/**
 * Whether a period lies entirely within a focus window. A period shown in full on an
 * earlier page is verified from those same edits without a page of its own.
 */
export const periodWithinFocus = ({
  periodStart,
  periodEnd,
  focusStart,
  focusEnd,
}: {
  periodStart: Dayjs;
  periodEnd: Dayjs;
  focusStart: Dayjs;
  focusEnd: Dayjs;
}): boolean =>
  !periodStart.isBefore(focusStart) && !periodEnd.isAfter(focusEnd);

/**
 * Whether a period's own range genuinely overlaps a focus window — the gate for
 * capturing a period's slice passively (not via its own hop) from a hop whose focus
 * happens to reach it.
 */
export const periodOverlapsFocus = ({
  periodStart,
  periodEnd,
  focusStart,
  focusEnd,
}: {
  periodStart: Dayjs;
  periodEnd: Dayjs;
  focusStart: Dayjs;
  focusEnd: Dayjs;
}): boolean => periodStart.isBefore(focusEnd) && periodEnd.isAfter(focusStart);

/**
 * A period's own slice of a bring-in focus's captured events: its current and original
 * events, each located by its own start, and whether that slice was edited — a diff
 * against the focus's own seed, or `previouslyEdited` already being true.
 * `previouslyEdited` carries forward an earlier hop's own finding for the same period,
 * so a later hop reseeded with that edit — which then diffs clean against it — can't
 * erase the record of it.
 */
export const periodSliceFromFocus = <
  Event extends { id: string; start: Dayjs },
>({
  periodStart,
  periodEnd,
  focusEvents,
  focusOriginalEvents,
  focusSeedEventsById,
  eventDiffersFromSeed,
  previouslyEdited,
}: {
  periodStart: Dayjs;
  periodEnd: Dayjs;
  focusEvents: Event[];
  focusOriginalEvents: Event[];
  focusSeedEventsById: Map<string, Event>;
  eventDiffersFromSeed: (event: Event, seed: Event | undefined) => boolean;
  previouslyEdited: boolean;
}): { events: Event[]; originalEvents: Event[]; edited: boolean } => {
  const inPeriod = (event: Event): boolean =>
    eventStartsInPeriod({ start: event.start, periodStart, periodEnd });
  const events = focusEvents.filter(inPeriod);
  const originalEvents = focusOriginalEvents.filter(inPeriod);
  const edited =
    previouslyEdited ||
    eventsEditedAgainstSeed({
      events,
      seedEvents: [...focusSeedEventsById.values()].filter(inPeriod),
      seedById: focusSeedEventsById,
      eventDiffersFromSeed,
    });
  return { events, originalEvents, edited };
};
